import React, { useEffect } from 'react';
import Appearance from 'styles/Appearance.js';
import Utils from 'files/Utils.js';
import config from 'files/api.js';

const Footer = ({ onNavigate, utils }) => {

    // the recaptcha notice below stands in for google's floating badge, which is hidden only while this footer is showing
    useEffect(() => {
        document.body.classList.add('recaptcha-notice-shown');
        return () => document.body.classList.remove('recaptcha-notice-shown');
    }, []);

    const handleNavClick = (section) => {
        if(onNavigate) {
            onNavigate(section);
        }
        Utils.scrollToElement(section);
    };

    const currentYear = new Date().getFullYear();

    const goToRoute = (route) => () => onNavigate && onNavigate({ route });

    const footerSections = [
        {
            title: 'Company',
            links: [
                { label: 'About', action: goToRoute('/about') },
                { label: 'Products', action: () => handleNavClick('products') },
                { label: 'Careers', action: goToRoute('/careers') },
                { label: 'Contact Us', action: goToRoute('/contact') },
            ]
        },
        {
            title: 'Support',
            links: [
                { label: 'Support Center', action: goToRoute('/support') },
                { label: 'FAQs', action: goToRoute('/faq') },
                { label: 'Shipping', action: goToRoute('/shipping') },
                { label: 'Returns', action: goToRoute('/returns') },
            ]
        },
        {
            title: 'Contact Us',
            isContact: true,
        }
    ];

    const socialLinks = [/*
        { icon: 'fab fa-facebook-f', href: config.social.facebook },
        { icon: 'fab fa-instagram', href: config.social.instagram },
        { icon: 'fab fa-twitter', href: config.social.twitter },
        { icon: 'fab fa-linkedin-in', href: config.social.linkedin },
    */];

    return (
        <footer id={'contact'} style={{
            backgroundColor: Appearance.colors.dark(),
            color: Appearance.colors.white,
            paddingTop: Utils.isMobile() ? 40 : 60,
        }}>
            <div style={{
                maxWidth: 1200,
                margin: '0 auto',
                padding: Utils.isMobile() ? '0 20px' : '0 40px',
            }}>

                {/* Main Footer Content */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: Utils.isMobile() ? '1fr' : '1.5fr 1fr 1fr 1.5fr',
                    gap: Utils.isMobile() ? 40 : 60,
                    paddingBottom: 40,
                }}>

                    {/* Brand Column */}
                    <div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            marginBottom: 16,
                        }}>
                            <img 
                            src={'/images/aqua-link-light.png'}
                            style={{
                                height: 25,
                                objectFit: 'contain',
                                width: 'auto'
                            }} />
                        </div>
                        <p style={{
                            ...Appearance.textStyles.body({ color: 'rgba(255,255,255,0.6)', fontSize: 14 }),
                            maxWidth: 280,
                            marginBottom: 20,
                        }}>
                            {config.store.description}
                        </p>

                        {/* Social Links */}
                        <div style={{ display: 'flex', gap: 12 }}>
                            {socialLinks.map((social, index) => (
                                <a
                                    key={index}
                                    href={social.href}
                                    target={'_blank'}
                                    rel={'noopener noreferrer'}
                                    style={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: 18,
                                        backgroundColor: 'rgba(255,255,255,0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'background-color 0.3s ease',
                                    }}>
                                    <i className={social.icon} style={{ color: 'white', fontSize: 14 }} />
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Link Columns */}
                    {footerSections.map((section, index) => (
                        <div key={index}>
                            <span style={{
                                fontFamily: Appearance.fonts.body,
                                fontWeight: 700,
                                fontSize: 14,
                                color: Appearance.colors.white,
                                marginBottom: 20,
                                display: 'block',
                            }}>
                                {section.title}
                            </span>

                            {section.isContact ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    <span style={{
                                        ...Appearance.textStyles.body({ color: 'rgba(255,255,255,0.6)', fontSize: 14 }),
                                    }}>
                                        {config.store.email}
                                    </span>
                                    <span style={{
                                        ...Appearance.textStyles.body({ color: 'rgba(255,255,255,0.6)', fontSize: 14 }),
                                    }}>
                                        {config.store.phone}
                                    </span>
                                    <a
                                        href={`mailto:${config.store.email}`}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            marginTop: 8,
                                            padding: '10px 20px',
                                            borderRadius: 20,
                                            border: `1px solid ${Appearance.colors.primary()}`,
                                            color: Appearance.colors.primary(),
                                            fontFamily: Appearance.fonts.body,
                                            fontWeight: 600,
                                            fontSize: 13,
                                            transition: 'all 0.3s ease',
                                        }}>
                                        {'Request a Callback'}
                                        <i className={'fas fa-arrow-right'} style={{ fontSize: 12 }} />
                                    </a>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    {section.links.map((link, linkIndex) => (
                                        <span
                                            key={linkIndex}
                                            onClick={link.action || undefined}
                                            style={{
                                                ...Appearance.textStyles.body({ color: 'rgba(255,255,255,0.6)', fontSize: 14 }),
                                                cursor: 'pointer',
                                                transition: 'color 0.3s ease',
                                            }}>
                                            {link.label}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Copyright */}
                <div style={{
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    padding: '20px 0',
                    display: 'flex',
                    alignItems: Utils.isMobile() ? 'flex-start' : 'center',
                    justifyContent: 'space-between',
                    flexDirection: Utils.isMobile() ? 'column' : 'row',
                    gap: 12,
                }}>
                    <span style={{
                        ...Appearance.textStyles.caption({ color: 'rgba(255,255,255,0.4)' }),
                    }}>
                        {`Copyright © ${currentYear}. All Rights Reserved.`}
                    </span>
                    <span style={{
                        ...Appearance.textStyles.caption({ color: 'rgba(255,255,255,0.4)' }),
                    }}>
                        {'This site is protected by reCAPTCHA and the Google '}
                        <a href={'https://policies.google.com/privacy'} target={'_blank'} rel={'noopener noreferrer'} style={{ color: 'inherit', textDecoration: 'underline' }}>{'Privacy Policy'}</a>
                        {' and '}
                        <a href={'https://policies.google.com/terms'} target={'_blank'} rel={'noopener noreferrer'} style={{ color: 'inherit', textDecoration: 'underline' }}>{'Terms of Service'}</a>
                        {' apply.'}
                    </span>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
